import { useState, useEffect } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, useList, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { FormField } from "@/pages/Contacts";
import { Database, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { user_id: "", name: "", phone_number: "", status: "active", gateway: "meta_cloud", waba_id: "", phone_number_id: "", access_token: "", provisioning_notes: "" };

export default function AdminChannels() {
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch } = useList("/admin/channels");
  const [users, setUsers] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/users", { limit: 100 }).then((r) => setUsers(r.data || [])); }, []);

  const openCreate = () => { setForm(EMPTY); setEditing(null); setDrawer(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, access_token: "" }); setDrawer(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const { user_id, access_token, ...upd } = form;
        await api.put(`/admin/channels/${editing.id}`, upd);
        toast.success("Channel diperbarui.");
      } else {
        await api.post("/admin/channels", form);
        toast.success("Channel dibuat.");
      }
      setDrawer(false); refetch();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Channels" subtitle="Semua channel lintas user & provisioning manual."
        action={<Button onClick={openCreate} className="bg-[#FFDA1A] hover:bg-[#F5CE00] text-[#111111]" data-testid="admin-create-channel-button"><Plus className="w-4 h-4 mr-1.5" /> Channel Manual</Button>} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari nama, nomor..." testid="admin-channel-search" />
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-44 bg-white" data-testid="admin-channel-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua status</SelectItem><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem><SelectItem value="setup_required">Perlu Setup</SelectItem></SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={Database} title="Belum ada channel" description="Provisioning channel manual untuk user tertentu."
          action={<Button onClick={openCreate} className="bg-[#003399] hover:bg-[#002B80]"><Plus className="w-4 h-4 mr-1.5" /> Channel Manual</Button>} />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr><th className={thClass}>Channel</th><th className={`${thClass} hidden md:table-cell`}>Pemilik</th><th className={`${thClass} hidden lg:table-cell`}>Nomor</th><th className={thClass}>Status</th></tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} onClick={() => openEdit(c)} className={trClass} data-testid={`admin-channel-row-${c.id}`}>
                  <td className={`${tdFirstClass} font-medium text-[#111111]`}>{c.name}</td>
                  <td className={`${tdClass} hidden md:table-cell`}>{c.user?.name || "-"}</td>
                  <td className={`${tdClass} hidden lg:table-cell font-mono text-[13px] text-[#003399]`}>{c.display_phone_number}</td>
                  <td className={tdClass}><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={drawer} onClose={() => setDrawer(false)} title={editing ? "Edit Channel" : "Channel Manual Baru"} description="Membuat channel di Serabi tidak membuat channel di provider." testid="admin-channel-drawer"
        footer={<><Button variant="outline" onClick={() => setDrawer(false)}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-[#003399] hover:bg-[#002B80]" data-testid="admin-channel-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button></>}>
        <div className="space-y-4">
          {!editing && (
            <div className="space-y-2">
              <Label>Pemilik (User) *</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="admin-channel-user"><SelectValue placeholder="Pilih user" /></SelectTrigger>
                <SelectContent>{users.filter((u) => u.role === "user").map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <FormField label="Nama Channel *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-channel-name" />
          <FormField label="Nomor Bisnis *" value={form.display_phone_number} onChange={(v) => setForm({ ...form, display_phone_number: v })} placeholder="+62 812..." testid="admin-channel-phone" />
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="admin-channel-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem><SelectItem value="setup_required">Perlu Setup</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="pt-2 border-t border-neutral-200">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Konfigurasi Internal (tidak tampil ke user)</p>
            <div className="space-y-3">
              <FormField label="Gateway" value={form.gateway} onChange={(v) => setForm({ ...form, gateway: v })} testid="admin-channel-gateway" />
              <FormField label="WABA ID" value={form.waba_id} onChange={(v) => setForm({ ...form, waba_id: v })} testid="admin-channel-waba" />
              <FormField label="Phone Number ID" value={form.phone_number_id} onChange={(v) => setForm({ ...form, phone_number_id: v })} testid="admin-channel-pnid" />
              {!editing && <FormField label="Access Token Provider" value={form.access_token} onChange={(v) => setForm({ ...form, access_token: v })} testid="admin-channel-token" />}
            </div>
          </div>
        </div>
      </RightDrawer>
    </div>
  );
}
